// ============================================================
// INTERVAL REGISTRY — Fase 12.1
// Registro global de timers, intervals, RAFs e timeouts
// Detecta acumulação e permite auditoria de recursos ativos
// ============================================================

export type TrackedType = 'interval' | 'timeout' | 'raf' | 'subscription' | 'listener'

export interface TrackedResource {
  id:        number
  type:      TrackedType
  label:     string
  createdAt: number
  stack?:    string   // captura stacktrace em dev
}

// ─── Singleton registry ───────────────────────────────────
const _registry = new Map<number, TrackedResource>()
let   _nextId   = 1
const _isDev    = import.meta.env.DEV

function captureStack(): string | undefined {
  if (!_isDev) return undefined
  try {
    throw new Error()
  } catch (e) {
    return (e as Error).stack?.split('\n').slice(2, 5).join('\n')
  }
}

// ─── Public API ───────────────────────────────────────────
export function registerInterval(
  native: ReturnType<typeof setInterval>,
  label:  string,
): ReturnType<typeof setInterval> {
  const id = _nextId++
  _registry.set(id, { id, type: 'interval', label, createdAt: Date.now(), stack: captureStack() })
  // Store native handle alongside our ID via weak coupling
  ;(native as any)['__reg_id'] = id
  return native
}

export function registerTimeout(
  native: ReturnType<typeof setTimeout>,
  label:  string,
): ReturnType<typeof setTimeout> {
  const id = _nextId++
  _registry.set(id, { id, type: 'timeout', label, createdAt: Date.now(), stack: captureStack() })
  ;(native as any)['__reg_id'] = id
  return native
}

export function registerRAF(
  rafHandle: number,
  label:     string,
): number {
  const id = _nextId++
  _registry.set(id, { id, type: 'raf', label, createdAt: Date.now(), stack: captureStack() })
  ;(rafHandle as any)['__reg_id'] = id
  return rafHandle
}

export function registerResource(type: TrackedType, label: string): number {
  const id = _nextId++
  _registry.set(id, { id, type, label, createdAt: Date.now(), stack: captureStack() })
  return id
}

export function unregisterResource(id: number): void {
  _registry.delete(id)
}

export function unregisterByNativeHandle(native: unknown): void {
  const regId = (native as any)?.['__reg_id'] as number | undefined
  if (regId !== undefined) _registry.delete(regId)
}

// ─── Inspection ───────────────────────────────────────────
export function getRegistrySnapshot(): TrackedResource[] {
  return Array.from(_registry.values())
}

export function getRegistryByType(type: TrackedType): TrackedResource[] {
  return Array.from(_registry.values()).filter(r => r.type === type)
}

export function getRegistryCount(): Record<TrackedType, number> {
  const counts: Record<TrackedType, number> = {
    interval: 0, timeout: 0, raf: 0, subscription: 0, listener: 0,
  }
  for (const r of _registry.values()) counts[r.type]++
  return counts
}

export function getOrphanedResources(maxAgeMs = 300_000): TrackedResource[] {
  const now = Date.now()
  return Array.from(_registry.values()).filter(r => now - r.createdAt > maxAgeMs)
}

export function clearRegistry(): void {
  _registry.clear()
}
